import { axiosPrivate } from 'app/utils';
import helpers from '../helpers';

import services from './services';
import consultationRooms from './consultation-rooms';
import managedRepeats from './managed-repeats';
import messages from './messages';
import patients from './patients';
import linkRequests from './link-requests';
import getPharmacyPhotoOrders from './get-pharmacy-photo-orders';
import deliverySubscriptions from './delivery-subscriptions';
import stats from './stats';

export default {
  async getPharmacy({ pharmacyId }) {
    const response = await axiosPrivate.get(`/pharmacies/${pharmacyId}`);

    if (response.data.error) helpers.throwError(response.data.error);
    return response.data;
  },

  async updatePharmacyDeliveryOptions({ pharmacyId, deliveryOptions }) {
    const response = await axiosPrivate.put(`/pharmacies/v2/${pharmacyId}`, {
      delivery_options: deliveryOptions,
    });

    // send slack message
    await axiosPrivate.post(`/pharmacies/${pharmacyId}/send-clinic-activate-message/`);

    if (response.data.error) helpers.throwError(response.data.error);
    return response.data;
  },

  async getUserPharmacies({ userId }) {
    const response = await axiosPrivate.get(`/pharmacists/${userId}/pharmacies`, {
      headers: { 'accept-version': '2.0.0' },
    });
    if (response.data.error) helpers.throwError(response.data.error);

    return {
      pharmacies: response.data.data,
    };
  },

  async togglePatientThreadPin({ pharmacyId, patientId, isPinned }) {
    const response = await axiosPrivate.put(
      `/pharmacies/${pharmacyId}/patients/${patientId}/pin-message`,
      {
        is_pinned: isPinned,
      }
    );
    if (response.data.error) helpers.throwError(response.data.error);
    if (!response.data.success) throw new Error('Failed to pin patient thread');
  },

  async getPredefinedMessages({ pharmacyId }) {
    const response = await axiosPrivate.get(`/pharmacies/${pharmacyId}/predefined-messages`);

    if (response.data.error) helpers.throwError(response.data.error);
    return response.data.data;
  },

  async getPharmacyInviteLink({ pharmacyId }) {
    const response = await axiosPrivate.post(`/tools/short-url`, { pharmacy_id: pharmacyId });

    if (response.data.error) helpers.throwError(response.data.error);
    return response.data.data;
  },

  async uploadDmrPharmacyInviteCsv({ pharmacyId, formData }) {
    const response = await axiosPrivate.put(
      `/pharmacies/${pharmacyId}/batch-dmr-import`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );

    if (response.data.error) helpers.throwError(response.data.error);
    return response.data.data;
  },

  async invitePatient({ pharmacyId, formData }) {
    const response = await axiosPrivate.post(`/pharmacies/${pharmacyId}/patients`, formData);

    if (response.data.error) helpers.throwError(response.data.error);
    return response.data.data;
  },

  async getPatientNotes({ pharmacyId, patientId }) {
    const response = await axiosPrivate.get(
      `/pharmacies/${pharmacyId}/patients/${patientId}/notes`
    );

    if (response.data.error) helpers.throwError(response.data.error);
    return response.data;
  },

  async createNote({ pharmacyId, patientId, text }) {
    const response = await axiosPrivate.post(
      `/pharmacies/${pharmacyId}/patients/${patientId}/notes`,
      {
        content: text,
      }
    );

    if (response.data.error) helpers.throwError(response.data.error);
    return response.data;
  },

  async updateNote({ pharmacyId, patientId, noteId, text }) {
    const response = await axiosPrivate.put(
      `/pharmacies/${pharmacyId}/patients/${patientId}/notes/${noteId}`,
      {
        content: text,
      }
    );

    if (response.data.error) helpers.throwError(response.data.error);
    return response.data;
  },

  async updatePharmacyById({ pharmacyId, data }) {
    const response = await axiosPrivate.put(`/v3/pharmacist/pharmacies/${pharmacyId}`, data);

    if (response.data.error) helpers.throwError(response.data.error);
    return response.data;
  },

  async getPharmacyById({ pharmacyId }) {
    const response = await axiosPrivate.get(`/v3/pharmacist/pharmacies/${pharmacyId}`);

    if (response.data.error) helpers.throwError(response.data.error);
    return response.data;
  },

  async getPharmacyUberStoreStatus({ pharmacyId }) {
    try {
      const response = await axiosPrivate.get(
        `/v3/pharmacist/pharmacies/${pharmacyId}/uber-store-status`
      );

      if (response.data.error) helpers.throwError(response.data.error);
      return response.data;
    } catch (error) {
      // When pharmacy is not configured (404), return default response
      if (error?.response?.status === 404) {
        return {
          status: null,
          pharmacyId,
        };
      }
      throw error;
    }
  },

  async enablePharmacyUber({ pharmacyId }) {
    const response = await axiosPrivate.post(`/v3/pharmacist/pharmacies/${pharmacyId}/enable-uber`);

    if (response.data.error) helpers.throwError(response.data.error);
    return response.data;
  },

  async getAddOns({ serviceId, serviceType }) {
    const response = await axiosPrivate.get(
      `/v3/pharmacist/add-ons?serviceId=${serviceId}&serviceType=${serviceType}`
    );

    if (response.data.error) helpers.throwError(response.data.error);
    return response.data;
  },

  async deleteAddOn({ addOnId }) {
    const response = await axiosPrivate.delete(`/v3/pharmacist/add-ons/${addOnId}`);

    if (response.data.error) helpers.throwError(response.data.error);
    return response.data;
  },

  async updateAddOn({ addOnId, payload }) {
    const response = await axiosPrivate.patch(`/v3/pharmacist/add-ons/${addOnId}`, payload);

    if (response.data.error) helpers.throwError(response.data.error);
    return response.data;
  },

  async createAddOn({ payload }) {
    const response = await axiosPrivate.post(`/v3/pharmacist/add-ons`, payload);

    if (response.data.error) helpers.throwError(response.data.error);
    return response.data;
  },

  ...services,
  ...consultationRooms,
  ...managedRepeats,
  ...messages,
  ...patients,
  ...linkRequests,
  ...getPharmacyPhotoOrders,
  ...deliverySubscriptions,
  ...stats,
};
